import { useEffect, useMemo, useState } from 'react';
import { useWorkspace } from '@/app/providers/workspace-provider';
import {
  FOLLOW_UP_CHOICE_LABELS,
  createFollowUpRepository,
  type FollowUpChoice,
} from '@/data/repositories/follow-up-repository';
import type { Uuid } from '@/data/db';
import { Button } from '@/components/primitives/button';
import { Dialog } from '@/components/primitives/dialog';
import { Field, TextInput } from '@/components/primitives/field';
import { useToast } from '@/components/feedback/toast';
import { nowLocalDateTime } from '@/lib/clock';
import { cn } from '@/lib/cn';

const CHOICE_HINTS: Record<FollowUpChoice, string> = {
  awaiting_reply: '狀態改成等待回覆，開始計算等了幾天。',
  remind_again: '用同一位主管、同樣的方式與重點，再排一次提醒。',
  closed: '狀態改成已結案，卡片移入已完成。',
  cancelled: '狀態改成已取消，卡片移入已完成並標示。',
  undecided: '什麼都不改，之後想到再處理。',
};

interface ReminderFollowUpPanelProps {
  reminderId: Uuid;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * spec 12.1 的「這件事結案了嗎」。
 *
 * 文案不催促也不責備；「先不決定」與其他選項在視覺上同等，
 * 不做成次要或灰暗的樣式。
 */
export function ReminderFollowUpPanel({
  reminderId,
  open,
  onOpenChange,
}: ReminderFollowUpPanelProps) {
  const { db, save } = useWorkspace();
  const toast = useToast();
  const repository = useMemo(() => createFollowUpRepository(db), [db]);

  const [choices, setChoices] = useState<FollowUpChoice[] | null>(null);
  const [selected, setSelected] = useState<FollowUpChoice | null>(null);
  const [nextFollowUpAtLocal, setNextFollowUpAtLocal] = useState('');
  const [remindAtLocal, setRemindAtLocal] = useState(() => nowLocalDateTime());

  useEffect(() => {
    let cancelled = false;
    void repository.availableFollowUpChoices(reminderId).then((available) => {
      if (!cancelled) setChoices(available);
    });
    return () => {
      cancelled = true;
    };
  }, [repository, reminderId]);

  async function apply(choice: FollowUpChoice) {
    try {
      await save.run(async () => {
        if (choice === 'awaiting_reply') {
          await repository.resolveFollowUpPrompt(reminderId, {
            choice,
            nextFollowUpAtLocal: nextFollowUpAtLocal || null,
          });
        } else if (choice === 'remind_again') {
          await repository.resolveFollowUpPrompt(reminderId, { choice, remindAtLocal });
        } else {
          await repository.resolveFollowUpPrompt(reminderId, { choice });
        }
      });

      if (choice !== 'undecided') {
        toast.show({
          message: `已記錄：${FOLLOW_UP_CHOICE_LABELS[choice]}`,
          tone: choice === 'closed' ? 'success' : 'default',
        });
      }
      onOpenChange(false);
    } catch (error) {
      toast.show({
        message: '沒有存成功',
        detail: error instanceof Error ? error.message : undefined,
        tone: 'danger',
      });
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // 面板可以直接關掉，關掉等同「先不決定」。
        if (!next) void apply('undecided');
        else onOpenChange(next);
      }}
      title="已記錄提醒。這件事結案了嗎？"
      description="選一個就好，之後隨時可以改。"
      size="md"
    >
      <div className="space-y-2">
        {(choices ?? []).map((choice) => {
          const isSelected = selected === choice;
          const needsInput = choice === 'awaiting_reply' || choice === 'remind_again';

          return (
            <div key={choice}>
              <button
                type="button"
                onClick={() => {
                  if (needsInput) {
                    setSelected(isSelected ? null : choice);
                  } else {
                    void apply(choice);
                  }
                }}
                className={cn(
                  'w-full rounded-[var(--radius-panel)] border px-4 py-3 text-left',
                  'transition-[background-color,border-color] duration-200 ease-[var(--ease-gentle)]',
                  isSelected
                    ? 'border-accent bg-accent-soft'
                    : 'border-border hover:border-border-strong hover:bg-surface-sunken',
                )}
              >
                <p className="text-[0.9375rem]">{FOLLOW_UP_CHOICE_LABELS[choice]}</p>
                <p className="text-[0.8125rem] text-text-muted">{CHOICE_HINTS[choice]}</p>
              </button>

              {isSelected && choice === 'awaiting_reply' && (
                <div className="mt-2 space-y-3 rounded-[var(--radius-panel)] bg-surface-sunken p-4">
                  <Field label="下一次追蹤時間" hint="可以留空，之後再補。">
                    {({ id }) => (
                      <TextInput
                        id={id}
                        type="datetime-local"
                        value={nextFollowUpAtLocal}
                        onChange={(event) => setNextFollowUpAtLocal(event.target.value)}
                      />
                    )}
                  </Field>
                  <Button variant="primary" size="sm" onClick={() => void apply(choice)}>
                    記下來
                  </Button>
                </div>
              )}

              {isSelected && choice === 'remind_again' && (
                <div className="mt-2 space-y-3 rounded-[var(--radius-panel)] bg-surface-sunken p-4">
                  <Field label="下一次提醒時間">
                    {({ id }) => (
                      <TextInput
                        id={id}
                        type="datetime-local"
                        value={remindAtLocal}
                        onChange={(event) => setRemindAtLocal(event.target.value)}
                      />
                    )}
                  </Field>
                  <Button variant="primary" size="sm" onClick={() => void apply(choice)}>
                    再排一次提醒
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Dialog>
  );
}
